import { OUTREACH_WA_GAP_SECONDS, type OutreachBatch } from '@secretario/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { markOutreach, outreachAction, OUTREACH_KEY } from '../../lib/api/outreach.js';
import { hapticSuccess, hapticTap } from '../../lib/native.js';
import {
  clock,
  firstName,
  outreachError,
  useOpenNext,
  useSecondsUntil,
} from '../../lib/outreach.js';
import { OutreachRing } from './OutreachRing.js';
import './outreach.css';

/**
 * A vez do WhatsApp num envio: a pergunta pelo anterior ("Mandou para a Carla?") e o botão do
 * próximo, que segura os 60 s com o anel e a contagem vindos do servidor. O mesmo na página,
 * na folha da cápsula e no cartão da mesa.
 */
export function WhatsAppTurn({ batch }: { batch: OutreachBatch }) {
  const queryClient = useQueryClient();
  const openNext = useOpenNext();
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const next = batch.next;
  const waiting = useSecondsUntil(next?.hold === 'espera' ? next.readyAt : null);
  const ready = Boolean(next) && next?.hold !== 'teto_do_dia' && waiting === 0;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: OUTREACH_KEY });

  // Ficou pronto agora: uma vibração só (sem som).
  const wasReady = useRef(ready);
  useEffect(() => {
    if (ready && !wasReady.current && document.visibilityState === 'visible') hapticSuccess();
    wasReady.current = ready;
  }, [ready]);

  const mark = useMutation({
    mutationFn: (state: 'enviado' | 'pulado') => markOutreach(batch.id, batch.open!.itemId, state),
    onSuccess: refresh,
    onError: (failure) => setError(outreachError(failure)),
  });
  const resume = useMutation({
    mutationFn: () => outreachAction(batch.id, 'resume'),
    onSuccess: refresh,
    onError: (failure) => setError(outreachError(failure)),
  });

  if (batch.state === 'pausado')
    return (
      <div className="wt">
        <p className="wt-q">O envio está pausado. Nada sai até você retomar.</p>
        <button
          type="button"
          className="pgs-btn solid wt-go"
          disabled={resume.isPending}
          onClick={() => {
            hapticTap();
            setError(null);
            resume.mutate();
          }}
        >
          Retomar
        </button>
        {error && <p className="wt-err">{error}</p>}
      </div>
    );

  const open = batch.open;
  const go = () => {
    hapticTap();
    setError(null);
    setOpening(true);
    // Chamado direto no toque: a janela do WhatsApp nasce aqui (senão o navegador bloqueia).
    void openNext(batch.id)
      .catch((failure: unknown) => setError(outreachError(failure)))
      .finally(() => setOpening(false));
  };

  return (
    <div className="wt">
      {open && (
        <div className="wt-ask" role="group" aria-label={`Mandou para ${open.name}?`}>
          <p className="wt-q">
            Mandou para <b>{open.name}</b>?
          </p>
          <div className="wt-two">
            <button
              type="button"
              className="pgs-btn small"
              disabled={mark.isPending}
              onClick={() => mark.mutate('pulado')}
            >
              Não mandei
            </button>
            <button
              type="button"
              className="pgs-btn small"
              disabled={mark.isPending}
              onClick={() => mark.mutate('enviado')}
            >
              Mandei
            </button>
          </div>
        </div>
      )}
      {next ? (
        ready ? (
          <button type="button" className="pgs-btn wt-go wa" disabled={opening} onClick={go}>
            Abrir WhatsApp de {firstName(next.name)}
          </button>
        ) : next.hold === 'teto_do_dia' ? (
          <p className="wt-q">
            Hoje já foram todos os WhatsApp permitidos. {firstName(next.name)} é a próxima amanhã.
          </p>
        ) : (
          <button type="button" className="pgs-btn wt-go wa hold" disabled aria-live="off">
            <OutreachRing value={waiting / OUTREACH_WA_GAP_SECONDS} size={24} tone="wa" />
            <span>
              {firstName(next.name)} em {clock(waiting)}
            </span>
          </button>
        )
      ) : (
        !open && <p className="wt-q">Todos os WhatsApp deste envio já foram abertos.</p>
      )}
      {error && (
        <p className="wt-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
